import { createSlice, current, PayloadAction } from '@reduxjs/toolkit';
import {
  ActivityImagesDataType,
  LogQuantityQuestion,
  InsertNewHabitPayload,
  DeviceType
} from 'interfaces';
import {
  AiToneOptions,
  CUSTOM_ROUTINE_TRIGGER,
  FLAG,
  FONT_OPTION,
  Mode,
  PackEditorTabMode,
  THEME_OPTION
} from 'enums';
import {
  ACTIVITY_TYPES,
  DEFAULT_DURATION,
  HABIT_SETTING_FEATURES,
  ROUTINE_DESTINATION_POSITION,
  ROUTINE_PLAYING_TYPE,
  SPLICE,
  TABS,
  USER_SETTINGS_KEYS,
  EMPTY_STRING,
  ROUTINE,
  TAB,
  ACTIVITY_SEQUENCE_TYPE,
  CUSTOM_ROUTINE_FEATURE,
  ACTIVITY_TEMPLATE_ID
} from 'constants/general';
import {
  DEFAULT_LOCAL_DEVICE_SETTINGS,
  INIT_CUSTOM_ROUTINE,
  INIT_NEW_HABIT
} from 'assets/data';
import { v4 as uuid } from 'uuid';
import {
  getLibraryActivities,
  getRoutineSummary,
  getUserSettings,
  initUserSettings,
  saveUserSettings,
  getUserStatsLeaderBoard,
  updateLocalDeviceSettings
} from './extra';
import {
  filterOutCutoffTimeAndTutorial,
  getActivitiesOfSettings,
  updateRoutineInSettings
} from 'utils/helpers';
import {
  appendActivityCopyToRoutine,
  toRoutineDestinationActivity
} from 'utils/routine-destination.util';
import {
  normalizeActivityDaysOfWeek,
  normalizeSettingsType
} from 'utils/daysOfWeek.util';
import {
  createRoutineActivityFromHabitPack,
  getActivitiesForUnlockTarget,
  packRoutineCategoryToActivityType,
  PackRoutineCategory
} from 'utils/habit-pack.util';
import { omitKeys } from 'utils/helpers/omitKeys';
import { initialSettingState } from 'store/initial-states';
import {
  ActivityType,
  ChoiceType,
  Tutorial
} from 'interfaces/common.interface';
import {
  ConfigureTabsPayload,
  CopyActivityToRoutinePayload,
  CustomRoutineType,
  FeedbackType,
  RoutineTimeValidation,
  RoutineType,
  SettingsType,
  UnlockHabitPackTarget,
  ValidationError
} from 'interfaces/setting.interface';
import {
  classifyRoutineSuggestions,
  filterOutCustomTiming,
  getRoutineValue,
  unifyRoutineActivities
} from 'utils/routine-suggestion.util';

interface AddHabitPayload {
  routine: string;
  activity: ActivityType;
}

const setting = createSlice({
  name: 'setting',
  initialState: initialSettingState,
  reducers: {
    changeIsSettingLoading: (state, action) => {
      state.isSettingLoading = action.payload;
    },
    changeIsSettingLoaded: (state, action) => {
      state.isSettingLoaded = action.payload;
    },
    updateShutdownTime: (state, { payload }) => {
      state.currentSettings.shutdown_time = payload;
    },
    updateStartupTime: (state, { payload }) => {
      state.currentSettings.startup_time = payload;
    },
    changeBreakingInterval: (state, { payload }) => {
      state.currentSettings.break_after_minutes = payload;
    },
    updateActivityFromMoreOptions: (
      state,
      {
        payload: { type, position, value, customRoutinePosition }
      }: PayloadAction<{
        type: string;
        position: number;
        value: ActivityType;
        customRoutinePosition?: number;
      }>
    ) => {
      const activities = getActivitiesOfSettings(
        type,
        state,
        customRoutinePosition
      );
      activities[position] = normalizeActivityDaysOfWeek(value);
      if (type === ROUTINE.CUSTOM_ROUTINES) {
        state.currentSettings = {
          ...state.currentSettings,
          custom_routines: state.currentSettings.custom_routines?.map(
            (routine, idx) =>
              idx === customRoutinePosition
                ? { ...routine, standalone_activities: activities }
                : routine
          )
        };
      } else {
        state.currentSettings = {
          ...state.currentSettings,
          [type]: activities
        };
      }
    },
    updateSettingFeatures: (
      state,
      {
        payload: { type, position, feature, value, customRoutinePosition }
      }: PayloadAction<{
        type: string;
        position: number;
        feature: string;
        value:
          | string
          | number
          | boolean
          | string[]
          | ActivityImagesDataType[]
          | undefined;
        customRoutinePosition?: number;
      }>
    ) => {
      const activities = getActivitiesOfSettings(
        type,
        state,
        customRoutinePosition
      );
      switch (feature) {
        case HABIT_SETTING_FEATURES.NAME:
          activities[position].name = value as string;
          break;
        case HABIT_SETTING_FEATURES.EMOJI:
          activities[position].habit_icon = value as string;
          break;
        case HABIT_SETTING_FEATURES.DURATION:
          activities[position].duration_seconds = value as number;
          break;
        case HABIT_SETTING_FEATURES.COMPLETION_REQUIREMENTS: {
          if (value === undefined || value === null) {
            const {
              completion_requirements: _completionRequirements,
              ...restActivity
            } = activities[position];
            activities[position] = restActivity as ActivityType;
          } else {
            activities[position].completion_requirements = value as string;
          }
          break;
        }
      }
      if (type === ACTIVITY_TYPES.LIBRARY) {
        state.activityLibrary = [...activities];
      } else if (
        state.currentSettings.custom_routines?.length &&
        typeof customRoutinePosition === 'number'
      ) {
        state.currentSettings.custom_routines[
          customRoutinePosition
        ].standalone_activities = activities;
      } else {
        state.currentSettings = {
          ...state.currentSettings,
          [type]: activities
        };
      }
    },
    changeIncludeEveryBreak: (
      state,
      {
        payload: { position, data }
      }: PayloadAction<{ position: number; data: boolean }>
    ) => {
      if (state.currentSettings.break_activities) {
        state.currentSettings.break_activities[
          position
        ].include_in_every_break = data;
      }
    },
    changeIsOfficeFriendly: (
      state,
      {
        payload: { position, data }
      }: PayloadAction<{ position: number; data: boolean }>
    ) => {
      if (state.currentSettings.break_activities) {
        state.currentSettings.break_activities[position].is_office_friendly =
          data;
      }
    },
    changeCurrentSettings: (
      state,
      { payload }: PayloadAction<SettingsType>
    ) => {
      state.currentSettings = payload;
    },
    updatePreviousSettings: (
      state,
      { payload }: PayloadAction<SettingsType>
    ) => {
      state.previousSettings = payload;
    },
    newHabit: (
      state,
      {
        payload: {
          type,
          sequence_id,
          category,
          isFreeTimeHabit,
          freeTimeHabit,
          customRoutinePosition
        }
      }: PayloadAction<{
        type: string;
        sequence_id?: string;
        category?: string;
        isNewHabitAppended?: boolean;
        isFreeTimeHabit?: boolean;
        freeTimeHabit?: ActivityType;
        customRoutinePosition?: number;
      }>
    ) => {
      switch (type) {
        case ACTIVITY_TYPES.MORNING:
          if (state.currentSettings.morning_activities) {
            state.currentSettings.morning_activities.push({
              ...INIT_NEW_HABIT(),
              activity_sequence_id: sequence_id,
              activity_type: ACTIVITY_SEQUENCE_TYPE.MORNING
            });
          }
          break;
        case ACTIVITY_TYPES.EVENING:
          if (state.currentSettings.evening_activities) {
            state.currentSettings.evening_activities.push(
              isFreeTimeHabit && freeTimeHabit
                ? freeTimeHabit
                : {
                    ...INIT_NEW_HABIT(),
                    activity_sequence_id: sequence_id,
                    duration_seconds: DEFAULT_DURATION.EVENING,
                    activity_type: ACTIVITY_SEQUENCE_TYPE.EVENING
                  }
            );
          }
          break;
        case ACTIVITY_TYPES.BREAKING:
          if (state.currentSettings.break_activities) {
            state.currentSettings.break_activities.push({
              ...INIT_NEW_HABIT(),
              activity_sequence_id: sequence_id,
              duration_seconds: DEFAULT_DURATION.BREAKING,
              activity_type: ACTIVITY_TYPES.BREAKING
            });
          }
          break;
        case ACTIVITY_TYPES.LIBRARY:
          if (state.activityLibrary) {
            state.activityLibrary.push({
              ...INIT_NEW_HABIT(),
              duration_seconds: DEFAULT_DURATION.DEFAULT,
              category,
              activity_type: ACTIVITY_TYPES.LIBRARY
            });
          }
          break;
        case ACTIVITY_TYPES.CUSTOM_ROUTINES:
          if (customRoutinePosition !== undefined) {
            state.currentSettings.custom_routines = current(
              state.currentSettings?.custom_routines ?? []
            ).map((routine, idx) =>
              idx === customRoutinePosition
                ? {
                    ...routine,
                    standalone_activities: [
                      ...(routine?.standalone_activities ?? []),
                      {
                        ...INIT_NEW_HABIT(),
                        activity_sequence_id: sequence_id,
                        duration_seconds: DEFAULT_DURATION.STANDALONE,
                        activity_type: ACTIVITY_SEQUENCE_TYPE.STANDALONE
                      }
                    ]
                  }
                : routine
            );
          } else {
            state.currentSettings = {
              ...state.currentSettings,
              custom_routines: [
                ...(state.currentSettings.custom_routines ?? []),
                INIT_CUSTOM_ROUTINE()
              ]
            };
          }
          break;
        default:
          if (state.currentSettings.standalone_activities) {
            state.currentSettings.standalone_activities.push({
              ...INIT_NEW_HABIT(),
              activity_sequence_id: sequence_id,
              duration_seconds: DEFAULT_DURATION.STANDALONE,
              activity_type: ACTIVITY_SEQUENCE_TYPE.STANDALONE
            });
          }
      }
      state.isNewHabitAppended = true;
    },
    insertNewHabit: (
      state,
      {
        payload: {
          type,
          sequence_id,
          position,
          category,
          customRoutinePosition
        }
      }: PayloadAction<InsertNewHabitPayload>
    ) => {
      if (typeof customRoutinePosition === 'number') {
        state.currentSettings.custom_routines?.[
          customRoutinePosition
        ]?.standalone_activities?.splice(position, SPLICE.INSERT, {
          ...INIT_NEW_HABIT(),
          activity_sequence_id: sequence_id,
          duration_seconds: DEFAULT_DURATION.STANDALONE,
          activity_type: ACTIVITY_SEQUENCE_TYPE.STANDALONE
        });
      } else {
        switch (type) {
          case ACTIVITY_TYPES.MORNING:
            if (state.currentSettings.morning_activities) {
              state.currentSettings.morning_activities.splice(
                position,
                SPLICE.INSERT,
                {
                  ...INIT_NEW_HABIT(),
                  activity_sequence_id: sequence_id
                }
              );
            }
            break;
          case ACTIVITY_TYPES.EVENING:
            if (state.currentSettings.evening_activities) {
              state.currentSettings.evening_activities.splice(
                position,
                SPLICE.INSERT,
                {
                  ...INIT_NEW_HABIT(),
                  activity_sequence_id: sequence_id,
                  duration_seconds: DEFAULT_DURATION.EVENING
                }
              );
            }
            break;
          case ACTIVITY_TYPES.BREAKING:
            if (state.currentSettings.break_activities) {
              state.currentSettings.break_activities.splice(
                position,
                SPLICE.INSERT,
                {
                  ...INIT_NEW_HABIT(),
                  activity_sequence_id: sequence_id,
                  duration_seconds: DEFAULT_DURATION.BREAKING
                }
              );
            }
            break;
          case ACTIVITY_TYPES.LIBRARY:
            if (state.activityLibrary) {
              state.activityLibrary.splice(position, SPLICE.INSERT, {
                ...INIT_NEW_HABIT(),
                duration_seconds: DEFAULT_DURATION.LIBARARY,
                category
              });
            }
            break;
          default:
            if (state.currentSettings.standalone_activities) {
              state.currentSettings.standalone_activities.splice(
                position,
                SPLICE.INSERT,
                {
                  ...INIT_NEW_HABIT(),
                  activity_sequence_id: sequence_id,
                  duration_seconds: DEFAULT_DURATION.STANDALONE
                }
              );
            }
        }
      }
      state.isNewHabitAppended = false;
    },
    removeHabit: (
      state,
      {
        payload: { type, position, customRoutinePosition }
      }: PayloadAction<{
        type: string;
        position: number;
        customRoutinePosition?: number;
      }>
    ) => {
      if (typeof customRoutinePosition === 'number') {
        state.currentSettings.custom_routines?.[
          customRoutinePosition
        ]?.standalone_activities?.splice(position, SPLICE.DELETE);
      } else {
        switch (type) {
          case ACTIVITY_TYPES.MORNING:
            if (state.currentSettings.morning_activities) {
              state.currentSettings.morning_activities.splice(
                position,
                SPLICE.DELETE
              );
            }
            break;
          case ACTIVITY_TYPES.EVENING:
            if (state.currentSettings.evening_activities) {
              state.currentSettings.evening_activities.splice(
                position,
                SPLICE.DELETE
              );
            }
            break;
          case ACTIVITY_TYPES.BREAKING:
            if (state.currentSettings.break_activities) {
              state.currentSettings.break_activities.splice(
                position,
                SPLICE.DELETE
              );
            }
            break;
          case ACTIVITY_TYPES.LIBRARY:
            if (state.activityLibrary) {
              state.activityLibrary.splice(position, SPLICE.DELETE);
            }
            break;
          default:
            if (state.currentSettings.standalone_activities) {
              state.currentSettings.standalone_activities.splice(
                position,
                SPLICE.DELETE
              );
            }
        }
      }
    },
    cloneHabit: (
      state,
      {
        payload: {
          templateActivity,
          newActivityPosition,
          type,
          customRoutinePosition
        }
      }: PayloadAction<{
        templateActivity: ActivityType;
        newActivityPosition: number;
        type: string;
        customRoutinePosition?: number;
      }>
    ) => {
      const clonedActivity = normalizeActivityDaysOfWeek({
        ...templateActivity,
        id: uuid(),
        completion_requirements:
          templateActivity.completion_requirements ?? undefined
      });

      switch (type) {
        case ACTIVITY_TYPES.MORNING:
          state.currentSettings.morning_activities?.splice(
            newActivityPosition,
            SPLICE.CLONE,
            clonedActivity
          );
          break;
        case ACTIVITY_TYPES.EVENING:
          state.currentSettings.evening_activities?.splice(
            newActivityPosition,
            SPLICE.CLONE,
            clonedActivity
          );
          break;
        case ACTIVITY_TYPES.BREAKING:
          state.currentSettings.break_activities?.splice(
            newActivityPosition,
            SPLICE.CLONE,
            clonedActivity
          );
          break;
        case ACTIVITY_TYPES.STANDALONE:
          state.currentSettings.standalone_activities?.splice(
            newActivityPosition,
            SPLICE.CLONE,
            clonedActivity
          );
          break;
        case ACTIVITY_TYPES.LIBRARY:
          state.activityLibrary.splice(newActivityPosition, 0, clonedActivity);
          break;
        case ACTIVITY_TYPES.CUSTOM_ROUTINES:
          if (typeof customRoutinePosition === 'number') {
            state.currentSettings.custom_routines =
              state.currentSettings.custom_routines?.map((routine, idx) =>
                idx === customRoutinePosition
                  ? {
                      ...routine,
                      standalone_activities: [
                        ...(routine.standalone_activities?.slice(
                          0,
                          newActivityPosition
                        ) ?? []),
                        clonedActivity,
                        ...(routine.standalone_activities?.slice(
                          newActivityPosition + 1
                        ) ?? [])
                      ]
                    }
                  : routine
              );
          }
          break;
      }
    },
    changeChoiceType: (
      state,
      {
        payload: { position, data }
      }: PayloadAction<{ position: number; data: string }>
    ) => {
      if (state.currentSettings.break_activities) {
        state.currentSettings.break_activities[position].choice_type = data;
      }
    },
    changeChoices: (
      state,
      {
        payload: { position, data }
      }: PayloadAction<{ position: number; data: ChoiceType[] }>
    ) => {
      if (state.currentSettings.break_activities) {
        state.currentSettings.break_activities[position].choices = data;
      }
    },
    changeActivityOrder: (
      state,
      {
        payload: { type, data, customRoutinePosition }
      }: PayloadAction<{
        type: string;
        data: ActivityType[];
        customRoutinePosition?: number;
      }>
    ) => {
      switch (type) {
        case ACTIVITY_TYPES.MORNING:
          state.currentSettings.morning_activities = data;
          break;
        case ACTIVITY_TYPES.EVENING:
          state.currentSettings.evening_activities = data;
          break;
        case ACTIVITY_TYPES.BREAKING:
          state.currentSettings.break_activities = data;
          break;
        case ACTIVITY_TYPES.LIBRARY:
          state.activityLibrary = data;
          break;
        case ACTIVITY_TYPES.CUSTOM_ROUTINES:
          if (
            state.currentSettings.custom_routines?.length &&
            typeof customRoutinePosition === 'number'
          ) {
            state.currentSettings.custom_routines[
              customRoutinePosition
            ].standalone_activities = data;
          }

          break;
        default:
          state.currentSettings.standalone_activities = data;
      }
    },
    changePackName: (state, action: PayloadAction<string>) => {
      state.currentSettings.pack_name = action.payload;
    },
    changePackDescription: (state, action: PayloadAction<string>) => {
      state.currentSettings.description = action.payload;
    },
    changePackDescriptionVideo: (state, action: PayloadAction<string>) => {
      state.currentSettings.description_video_url = action.payload;
    },
    changePackWelcomeMessage: (state, action: PayloadAction<string>) => {
      state.currentSettings.welcome_message = action.payload;
    },
    changePackWelcomeMessageVideo: (state, action: PayloadAction<string>) => {
      state.currentSettings.welcome_video_url = action.payload;
    },
    changePackCreatorName: (state, action: PayloadAction<string>) => {
      state.currentSettings.creator_name = action.payload;
    },
    changePackIsFeaturedStatus: (state, action: PayloadAction<boolean>) => {
      state.currentSettings.is_featured = action.payload;
    },
    changeIsTextEditorWithinCharacterLimit: (
      state,
      action: PayloadAction<boolean>
    ) => {
      state.isTextEditorWithinCharacterLimit = action.payload;
    },
    changePackFeaturedForOnboardingStatus: (
      state,
      action: PayloadAction<boolean>
    ) => {
      state.currentSettings.featured_for_onboarding = action.payload;
    },
    changePackMarketplaceApprovalStatus: (
      state,
      action: PayloadAction<boolean>
    ) => {
      state.currentSettings.marketplace_approval_status = action.payload;
    },
    updateRecentHabitIcons: (state, action: PayloadAction<string[]>) => {
      state.recentHabitIcons = action.payload;
    },
    addRecentHabitIcon: (state, action: PayloadAction<string>) => {
      state.recentHabitIcons = [
        action.payload,
        ...state.recentHabitIcons
      ].slice(0, 5);
    },
    sortRecentHabitIcons: (state, action: PayloadAction<string>) => {
      state.recentHabitIcons = [
        action.payload,
        ...state.recentHabitIcons.filter((icon) => icon !== action.payload)
      ];
    },
    updatePlatform: (state, { payload }: PayloadAction<string | undefined>) => {
      if (payload) {
        state.platform = payload;
      }
    },
    setGeneratorMode: (state, { payload }: PayloadAction<Mode>) => {
      state.mode = payload;
    },
    addInstalledApps: (
      state,
      { payload }: PayloadAction<{ installedApps: string[] }>
    ) => {
      state.suggestions = payload?.installedApps ?? [];
    },
    configureTabs: (
      state,
      {
        payload: {
          mode,
          routine: customRoutine,
          routineSuggestionPayload,
          routineSuggestions
        }
      }: PayloadAction<ConfigureTabsPayload>
    ) => {
      switch (mode) {
        case Mode.CUSTOM:
          {
            let tabs = TABS.CUSTOM_ROUTINE;
            if (customRoutine === ROUTINE.MORNING) {
              tabs = [TAB.MORNING];
            } else if (customRoutine === ROUTINE.BREAKING) {
              tabs = [TAB.MICRO_BREAKS];
            } else if (customRoutine === ROUTINE.EVENING) {
              tabs = [TAB.EVENING];
            } else if (customRoutine === ROUTINE.SUGGESTION) {
              //@Description: applied when both routines selected in routine suggestions embedded UI
              const shouldSelectBothRoutines = filterOutCustomTiming(
                routineSuggestionPayload?.routines || []
              ).every((routine) => routine.value);
              const { morning_suggestions, evening_suggestions } =
                classifyRoutineSuggestions(routineSuggestions ?? {});
              const morning_activities =
                unifyRoutineActivities(morning_suggestions);
              const evening_activities =
                unifyRoutineActivities(evening_suggestions);

              if (shouldSelectBothRoutines) {
                state.currentSettings.morning_activities = morning_activities;
                state.currentSettings.evening_activities = evening_activities;
                state.previousSettings.morning_activities = morning_activities;
                state.previousSettings.evening_activities = evening_activities;
                tabs = [TAB.MORNING, TAB.EVENING];
              } else {
                state.activeTabIndex = 0; //@Description: reset tab index as there's only one tab
                const routine = getRoutineValue(
                  routineSuggestionPayload?.routines || []
                );
                if (routine === ROUTINE.MORNING) {
                  state.currentSettings.morning_activities = morning_activities;
                  state.currentSettings.evening_activities = [];
                  state.previousSettings.evening_activities = [];
                  state.previousSettings.morning_activities =
                    morning_activities;
                  tabs = [TAB.MORNING];
                } else {
                  state.currentSettings.morning_activities = [];
                  state.currentSettings.evening_activities = evening_activities;
                  state.previousSettings.morning_activities = [];
                  state.previousSettings.evening_activities =
                    evening_activities;
                  tabs = [TAB.EVENING];
                }
              }
            }
            state.mode = mode;
            state.tabs = tabs;
            state.customRoutine = customRoutine ?? EMPTY_STRING;
          }
          break;
        default:
          state.tabs = TABS.DEFAULT;
          state.customRoutine = EMPTY_STRING;
      }
    },
    configurePackEditorTabs: (
      state,
      { payload }: PayloadAction<PackEditorTabMode>
    ) => {
      switch (payload) {
        case PackEditorTabMode.ROUTINE:
          state.tabs = TABS.ROUTINE;
          break;
        case PackEditorTabMode.STANDALONE:
          state.tabs = TABS.STANDALONE;
          break;
        case PackEditorTabMode.FOCUS_MODE:
          state.tabs = TABS.FOCUS_MODE;
          break;
      }
      state.customRoutine = EMPTY_STRING;
    },
    updateError: (
      state,
      {
        payload
      }: PayloadAction<{
        message: string;
        status?: number;
        url?: string;
      }>
    ) => {
      state.error = payload;
    },
    updateRoutineCategory: (
      state,
      {
        payload: {
          type,
          position,
          destination_type,
          destination_position,
          destination_sequence_id,
          customRoutinePosition,
          destination_customRoutinePosition
        }
      }: PayloadAction<{
        type: string;
        position: number;
        destination_type: string;
        destination_position: string;
        destination_sequence_id: string;
        customRoutinePosition?: number;
        destination_customRoutinePosition?: number;
      }>
    ) => {
      let sourceRoutineActivities = getActivitiesOfSettings(
        type,
        state,
        customRoutinePosition
      );
      let destinationRoutineActivities = getActivitiesOfSettings(
        destination_type,
        state,
        destination_customRoutinePosition
      );
      const habit = sourceRoutineActivities[position];
      sourceRoutineActivities = sourceRoutineActivities.filter(
        (_: ActivityType, idx: number) => position !== idx
      );
      const movingHabit = toRoutineDestinationActivity(
        habit,
        destination_sequence_id
      );
      switch (destination_position) {
        case ROUTINE_DESTINATION_POSITION.START:
          destinationRoutineActivities = [
            movingHabit,
            ...destinationRoutineActivities
          ];
          break;
        case ROUTINE_DESTINATION_POSITION.END:
          destinationRoutineActivities = [
            ...destinationRoutineActivities,
            movingHabit
          ];
          break;
        default:
          destinationRoutineActivities.splice(
            Math.floor(destinationRoutineActivities.length / 2),
            0,
            movingHabit
          );
      }
      state.isShowMoreOptionsOpened = false;
      if (type === ACTIVITY_TYPES.CUSTOM_ROUTINES) {
        state.currentSettings = {
          ...state.currentSettings,
          custom_routines: state.currentSettings.custom_routines?.map(
            (routine, idx) =>
              idx === customRoutinePosition
                ? {
                    ...routine,
                    standalone_activities: sourceRoutineActivities
                  }
                : routine
          )
        };
      } else {
        state.currentSettings = {
          ...state.currentSettings,
          [type]: sourceRoutineActivities
        };
      }
      if (destination_type === ACTIVITY_TYPES.CUSTOM_ROUTINES) {
        state.currentSettings = {
          ...state.currentSettings,
          custom_routines: state.currentSettings.custom_routines?.map(
            (routine, idx) =>
              idx === destination_customRoutinePosition
                ? {
                    ...routine,
                    standalone_activities: destinationRoutineActivities
                  }
                : routine
          )
        };
      } else {
        state.currentSettings = {
          ...state.currentSettings,
          [destination_type]: destinationRoutineActivities
        };
      }
    },
    updateMoreOptions: (
      state,
      { payload }: PayloadAction<{ type: string; position: number }>
    ) => {
      state.moreOptions = payload;
    },
    updateIsShowMoreOptionsOpened: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.isShowMoreOptionsOpened = payload;
    },
    updateIsShowLibraryMoreOptionsOpened: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.isShowLibraryMoreOptionsOpened = payload;
    },
    updateIsTokenAddedToInterceptor: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.isTokenAddedToInterceptor = payload;
    },
    updateCutoffTimeForNonHighPriority: (
      state,
      { payload }: PayloadAction<string | undefined>
    ) => {
      if (payload && payload.trim() !== EMPTY_STRING) {
        state.currentSettings.cutoff_time_for_non_high_priority_activities =
          payload;
      } else {
        const {
          cutoff_time_for_non_high_priority_activities: _cutoff,
          ...rest
        } = state.currentSettings;
        state.currentSettings = rest;
      }
    },
    changeAreLibraryActivitiesLoading: (state, action) => {
      state.areLibraryActivitiesLoading = action.payload;
    },
    updateRoutinePlayingActivity: (
      state,
      {
        payload: { type, activity }
      }: PayloadAction<{ type: string; activity: ActivityType | null }>
    ) => {
      if (type === ROUTINE_PLAYING_TYPE.CURRENT) {
        state.currentPlayingActivity = activity;
      } else {
        state.nextPlayingActivity = activity;
      }
    },
    updateRoutinePlayerActivities: (
      state,
      {
        payload: { type, activities }
      }: PayloadAction<{ type: string; activities: ActivityType[] }>
    ) => {
      state.routinePlayerActivities = { type, activities };
    },
    updateIsRoutineSummaryFetching: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.isRoutineSummaryFetching = payload;
    },
    updateRoutineSummary: (
      state,
      { payload }: PayloadAction<{ date: string; summary: string }[]>
    ) => {
      state.routineSummary = payload;
    },
    updateIsDeviceRegistering: (state, { payload }: PayloadAction<boolean>) => {
      state.isDeviceRegistering = payload;
    },
    updateIsSavingRoutineCompletedActivity: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.isSavingRoutineCompletedActivity = payload;
    },
    updateIsShowRoutineModal: (state, { payload }: PayloadAction<boolean>) => {
      state.isShowRoutineModal = payload;
    },
    duplicateMorningOnEveningRoutine: (state) => {
      const activity_sequence_id: string = uuid();
      const activities = state.currentSettings.morning_activities?.map(
        (activity: ActivityType) => {
          let log_quantity_questions: LogQuantityQuestion[] = [];
          if (activity.log_quantity_questions?.length) {
            log_quantity_questions = activity.log_quantity_questions.map(
              (question) => ({
                ...question,
                linked_question_id: question.id
              })
            );
          }
          return {
            ...activity,
            activity_sequence_id,
            linked_activity_template_id: activity.id,
            log_quantity_questions
          };
        }
      );
      if (activities) {
        state.currentSettings.evening_activities = [...activities];
        state.isShowRoutineModal = false;
      }
    },
    createRandomActivityWithChoiceOfMorningRoutine: (
      state,
      {
        payload: { activityName, choices }
      }: PayloadAction<{ activityName: string; choices: ChoiceType[] }>
    ) => {
      const new_activity_sequence_id: string = uuid();
      let activity_sequence_id: string = new_activity_sequence_id;
      const {
        currentSettings: { break_activities }
      } = current(state);
      if (break_activities && break_activities?.length > 0) {
        activity_sequence_id =
          break_activities[0].activity_sequence_id ?? new_activity_sequence_id;
      }
      const updatedChoices = choices.map((activity) => ({
        ...activity,
        activity_sequence_id
      }));
      const randomActivity = {
        ...INIT_NEW_HABIT(),
        name: activityName,
        duration_seconds: DEFAULT_DURATION.BREAKING,
        choices: [...updatedChoices]
      };
      state.currentSettings.break_activities?.push(randomActivity);
      state.isShowRoutineModal = false;
    },
    updateIsFetchingNextRoutineActivity: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.isFetchingNextRoutineActivity = payload;
    },
    updateActiveTabIndex: (state, { payload }: PayloadAction<number>) => {
      state.activeTabIndex = payload;
    },
    updateIsSettingEditingFinished: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.isSettingEditingFinished = payload;
    },
    updateAreSettingsValid: (state, { payload }: PayloadAction<boolean>) => {
      state.areSettingsValid = payload;
    },
    updateActiveMarketTab: (state, { payload }: PayloadAction<number>) => {
      state.activeMarketTab = payload;
    },
    updateAreSettingsSaving: (state, { payload }: PayloadAction<boolean>) => {
      state.areSettingsSaving = payload;
    },
    resetActiveTabIndex: (state) => {
      state.activeTabIndex = 0; // Active tab for single-routine edit (morning/evening)
    },
    setHighlightShutdownTime: (state, { payload }: PayloadAction<boolean>) => {
      state.highlightShutdownTime = payload;
    },
    setHighlightCutoffTime: (state, { payload }: PayloadAction<boolean>) => {
      state.highlightCutoffTime = payload;
    },
    updateLanguage: (state, { payload }: PayloadAction<string | undefined>) => {
      if (payload) {
        state.language = payload;
      }
    },
    updateIsUnlockHabitPackModalShown: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.isUnlockHabitPackModalShown = payload;
      if (!payload) {
        state.habitPackUnlockTarget = null;
      }
    },
    openUnlockHabitPackModal: (
      state,
      { payload }: PayloadAction<UnlockHabitPackTarget>
    ) => {
      state.habitPackUnlockTarget = payload;
      state.isUnlockHabitPackModalShown = true;
    },
    unlockHabitPackActivity: (state) => {
      const target = state.habitPackUnlockTarget;
      if (target) {
        const activities = getActivitiesForUnlockTarget(
          state.currentSettings,
          target
        );
        const activity = activities?.[target.position];
        if (activity) {
          activities[target.position] = omitKeys(activity, [
            ACTIVITY_TEMPLATE_ID
          ]);
        }
      }
      state.habitPackUnlockTarget = null;
      state.isUnlockHabitPackModalShown = false;
    },
    copyActivity: (
      state,
      {
        payload: { activity, destination }
      }: PayloadAction<CopyActivityToRoutinePayload>
    ) => {
      const {
        destination_type,
        destination_sequence_id,
        destination_customRoutinePosition
      } = destination;
      updateRoutineInSettings({
        state,
        isCustom: destination_type === ACTIVITY_TYPES.CUSTOM_ROUTINES,
        routineType: destination_type,
        activities: appendActivityCopyToRoutine({
          routineActivities:
            getActivitiesOfSettings(
              destination_type,
              state,
              destination_customRoutinePosition
            ) ?? [],
          activity,
          destinationSequenceId: destination_sequence_id
        }),
        customRoutineIndex: destination_customRoutinePosition
      });
      state.isShowMoreOptionsOpened = false;
    },
    updateShowPrivacyPolicyModal: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.showPrivacyPolicyModal = payload;
    },
    updateAiTone: (state, { payload }: PayloadAction<AiToneOptions>) => {
      state.aiTone = payload;
    },
    updateShowSkipActivityModal: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.showSkipActivityModal = payload;
    },
    updateRoutinePlayerStep: (state, { payload }: PayloadAction<number>) => {
      state.routinePlayerStep = payload;
    },
    updateIsLeaderBoardFetching: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.isLeaderBoardFetching = payload;
    },
    updateIsRequestDataConfirmationModalShown: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.isRequestDataConfirmationModalShown = payload;
    },
    updateIsRequestDataProcessing: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.isRequestDataProcessing = payload;
    },
    updateIsAccountDeleteConfirmationModalShown: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.isAccountDeleteConfirmationModalShown = payload;
    },
    updateIsAccountDeleting: (state, { payload }: PayloadAction<boolean>) => {
      state.isAccountDeleting = payload;
    },
    updateIsAccountDeleted: (state, { payload }: PayloadAction<boolean>) => {
      state.isAccountDeleted = payload;
    },
    updateIsUserSettingsModified: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.isUserSettingsModified = payload;
    },
    updateThemeMode: (
      state,
      { payload }: PayloadAction<string | undefined>
    ) => {
      if (payload) {
        state.themeMode = payload as THEME_OPTION;
      }
    },
    updateFont: (state, { payload }: PayloadAction<string | undefined>) => {
      if (payload) {
        state.font = payload as FONT_OPTION;
      }
    },
    updateIsActivityLibraryUpdating: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.isActivityLibraryUpdating = payload;
    },
    updatePreviousActivityLibrary: (
      state,
      { payload }: PayloadAction<ActivityType[]>
    ) => {
      state.previousActivityLibrary = payload;
    },
    updateFlags: (state, { payload }: PayloadAction<FLAG[]>) => {
      state.flags = payload;
    },
    addFlag: (state, { payload }: PayloadAction<FLAG>) => {
      const modeFlags = [FLAG.GEEK_MODE, FLAG.SIMPLE_MODE];
      const isModeFlag = modeFlags.includes(payload);
      if (isModeFlag) {
        state.flags = state.flags.filter((flag) => !modeFlags.includes(flag));
        state.flags.push(payload);
      } else {
        if (!state.flags.includes(payload)) {
          state.flags.push(payload);
        }
      }
    },
    removeFlag: (state, { payload }: PayloadAction<FLAG>) => {
      state.flags = state.flags.filter((flag) => flag !== payload);
    },
    resetFlags: (state) => {
      state.flags = [];
    },
    updateIsPlatformCoursesSyncing: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.isPlatformCoursesSyncing = payload;
    },
    updateValidationErrors: (
      state,
      { payload }: PayloadAction<ValidationError[]>
    ) => {
      state.validationErrors = payload;
    },
    updateFeedback: (state, { payload }: PayloadAction<FeedbackType>) => {
      state.feedback = payload;
    },
    updateIsUserOnboarding: (state, { payload }: PayloadAction<boolean>) => {
      state.isUserOnboarding = payload;
    },
    updateCustomRoutines: (
      state,
      { payload }: PayloadAction<CustomRoutineType[]>
    ) => {
      state.currentSettings.custom_routines = payload;
    },
    removeCustomRoutine: (
      state,
      { payload: position }: PayloadAction<number>
    ) => {
      state.currentSettings.custom_routines =
        state.currentSettings.custom_routines?.filter(
          (_, idx) => idx !== position
        );
    },
    updateCustomRoutine: (
      state,
      {
        payload: { feature, position, value }
      }: PayloadAction<{
        feature: string;
        position: number;
        value: string | string[] | ActivityType[] | CUSTOM_ROUTINE_TRIGGER;
      }>
    ) => {
      const customRoutines =
        state.currentSettings.custom_routines?.slice() ?? [];
      switch (feature) {
        case CUSTOM_ROUTINE_FEATURE.NAME:
          customRoutines[position].name = value as string;
          break;
        case CUSTOM_ROUTINE_FEATURE.START_TIME:
          customRoutines[position].start_time = value as string;
          break;
        case CUSTOM_ROUTINE_FEATURE.END_TIME:
          customRoutines[position].end_time = value as string;
          break;
        case CUSTOM_ROUTINE_FEATURE.TRIGGER:
          customRoutines[position].trigger = value as CUSTOM_ROUTINE_TRIGGER;
          break;
        case CUSTOM_ROUTINE_FEATURE.DAYS:
          customRoutines[position].days_of_week = value as string[];
          break;
        default:
          customRoutines[position].standalone_activities =
            value as ActivityType[];
      }
      state.currentSettings.custom_routines = [...customRoutines];
    },
    updateDeviceID: (state, { payload }: PayloadAction<string | undefined>) => {
      state.device_id = payload;
    },
    updateRoutineTimeValidation: (
      state,
      { payload }: PayloadAction<RoutineTimeValidation>
    ) => {
      state.routineTimeValidation = payload;
    },
    updateIsSearchingDevice: (state, { payload }: PayloadAction<boolean>) => {
      state.isSearchingDevice = payload;
    },
    updateDevice: (state, { payload }: PayloadAction<DeviceType>) => {
      state.device = payload;
    },
    resetIsNewHabitAppended: (state) => {
      state.isNewHabitAppended = false;
    },
    updateIsNewCustomRoutineAppended: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.isNewCustomRoutineAppended = payload;
    },
    updateStatsPageActiveTabIndex: (
      state,
      { payload }: PayloadAction<number>
    ) => {
      state.statsPageActiveTabIndex = payload;
    },
    addHabitToRoutine(state, action: PayloadAction<AddHabitPayload>) {
      const { routine, activity } = action.payload;
      const key = (routine + '_activities') as RoutineType;
      const activityToAdd = {
        ...createRoutineActivityFromHabitPack(activity),
        activity_type: packRoutineCategoryToActivityType(
          routine as PackRoutineCategory
        )
      };
      state.currentSettings[key]?.push(activityToAdd);
    },
    resetActivityFromRoutineSuggestionsVideoUrls: (
      state,
      {
        payload: { routineType, position }
      }: PayloadAction<{ routineType: RoutineType; position: number }>
    ) => {
      const activities = state.currentSettings[routineType];
      const activity = activities?.[position];

      if (activity) {
        activity.video_urls = [];
      }
    },
    restoreActivityFromRoutineSuggestionsVideoUrls: (
      state,
      {
        payload: { routineType, position, urls }
      }: PayloadAction<{
        routineType: RoutineType;
        position: number;
        urls: string[];
      }>
    ) => {
      const activities = state.currentSettings[routineType];
      const activity = activities?.[position];

      if (activity) {
        activity.video_urls = urls;
      }
    },
    updateIsNewUserSigninProcess: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.isNewUserSigninProcess = payload;
    },
    updateIsFetchingDeviceSettings: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.isFetchingDeviceSettings = payload;
    },
    updateIsUpdatingDeviceSettings: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.isUpdatingDeviceSettings = payload;
    },
    updateIsShowPrivacyPolicyModal: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.isShowPrivacyPolicyModal = payload;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(initUserSettings.fulfilled, (state, { payload }) => {
        if (payload && Object.keys(payload).length) {
          let next: SettingsType = { ...payload };
          if (USER_SETTINGS_KEYS.HAS_EDITED_SETTINGS in next) {
            const { has_edited_settings: _hasEdited, ...rest } = next;
            next = rest;
          }
          if (
            !(
              USER_SETTINGS_KEYS.CUTOFF_TIME_FOR_NON_HIGH_PRIORITY_ACTIVITIES in
              next
            )
          ) {
            next = {
              ...next,
              cutoff_time_for_non_high_priority_activities: undefined
            };
          }
          const normalized = normalizeSettingsType(next);
          state.currentSettings = normalized;
          state.previousSettings = normalized;
        }
        state.isSettingLoaded = true;
        state.isSettingLoading = false;
        return state;
      })
      .addCase(initUserSettings.rejected, (state) => {
        state.isSettingLoaded = false;
      })
      .addCase(saveUserSettings.pending, (state) => {
        state.areSettingsSaving = true;
      })
      .addCase(saveUserSettings.fulfilled, (state) => {
        state.areSettingsSaving = false;
        const normalized = normalizeSettingsType(state.currentSettings);
        state.currentSettings = normalized;
        state.previousSettings = normalized;
        state.isUserSettingsModified = false;
      })
      .addCase(saveUserSettings.rejected, (state, action) => {
        if (action.meta?.condition) {
          return;
        }
        state.areSettingsSaving = false;
      })
      .addCase(getUserSettings.fulfilled, (state, { payload }) => {
        let base: SettingsType = { ...(payload as SettingsType) };
        if (USER_SETTINGS_KEYS.HAS_EDITED_SETTINGS in base) {
          const { has_edited_settings: _hasEdited, ...rest } = base;
          base = rest;
        }
        const [morning_activities, break_activities] =
          filterOutCutoffTimeAndTutorial(
            [...(base.morning_activities ?? [])],
            [...(base.break_activities ?? [])]
          );
        const evening_activities = [...(base.evening_activities ?? [])].map(
          ({ tutorial, ...rest }) => {
            return {
              ...rest,
              tutorial: (tutorial as unknown as Tutorial)?.id ?? tutorial
            };
          }
        );
        const merged: SettingsType = {
          ...base,
          morning_activities,
          break_activities,
          evening_activities
        };
        const normalized = normalizeSettingsType(merged);
        state.currentSettings = normalized;
        state.previousSettings = normalized;
        state.isSettingLoaded = true;
        state.isSettingLoading = false;
      })
      .addCase(getUserSettings.rejected, (state) => {
        state.isSettingLoading = false;
        state.isSettingLoaded = false;
      })
      .addCase(getLibraryActivities.fulfilled, (state, { payload }) => {
        const library = (payload ?? []).map((activity) =>
          normalizeActivityDaysOfWeek(activity)
        );
        state.activityLibrary = library;
        state.previousActivityLibrary = library;
        state.areLibraryActivitiesLoading = false;
      })
      .addCase(getLibraryActivities.rejected, (state) => {
        state.areLibraryActivitiesLoading = false;
      })
      .addCase(getRoutineSummary.fulfilled, (state, { payload }) => {
        state.routineSummary = payload;
        state.isRoutineSummaryFetching = false;
      })
      .addCase(getRoutineSummary.rejected, (state) => {
        state.isRoutineSummaryFetching = false;
      })
      .addCase(getUserStatsLeaderBoard.fulfilled, (state, { payload }) => {
        if (payload) {
          state.leaders = payload;
        }
        state.isLeaderBoardFetching = false;
      })
      .addCase(getUserStatsLeaderBoard.rejected, (state) => {
        state.isLeaderBoardFetching = false;
      })
      .addCase(updateLocalDeviceSettings.fulfilled, (state, { payload }) => {
        if (payload) {
          state.localDeviceSettings = {
            ...DEFAULT_LOCAL_DEVICE_SETTINGS,
            ...payload,
            Web: {
              ...DEFAULT_LOCAL_DEVICE_SETTINGS.Web,
              ...(payload.Web ?? {})
            }
          };
        }
        state.isFetchingDeviceSettings = false;
      })
      .addCase(updateLocalDeviceSettings.rejected, (state) => {
        state.isFetchingDeviceSettings = false;
      });
  }
});

export const {
  updateShutdownTime,
  updateStartupTime,
  updateSettingFeatures,
  changeBreakingInterval,
  changeIncludeEveryBreak,
  changeIsOfficeFriendly,
  removeHabit,
  newHabit,
  changeChoiceType,
  changeChoices,
  changeActivityOrder,
  changePackName,
  changePackDescription,
  changePackDescriptionVideo,
  changePackWelcomeMessage,
  changePackWelcomeMessageVideo,
  insertNewHabit,
  updatePreviousSettings,
  changeCurrentSettings,
  changeIsSettingLoading,
  changeIsSettingLoaded,
  setGeneratorMode,
  updatePlatform,
  addInstalledApps,
  configureTabs,
  configurePackEditorTabs,
  updateError,
  updateRoutineCategory,
  updateMoreOptions,
  updateIsShowMoreOptionsOpened,
  updateIsShowLibraryMoreOptionsOpened,
  updateIsTokenAddedToInterceptor,
  changePackCreatorName,
  updateCutoffTimeForNonHighPriority,
  changeAreLibraryActivitiesLoading,
  cloneHabit,
  updateRoutinePlayingActivity,
  updateRoutinePlayerActivities,
  updateIsRoutineSummaryFetching,
  updateRoutineSummary,
  updateIsDeviceRegistering,
  updateIsSavingRoutineCompletedActivity,
  updateIsShowRoutineModal,
  duplicateMorningOnEveningRoutine,
  createRandomActivityWithChoiceOfMorningRoutine,
  updateActivityFromMoreOptions,
  changePackIsFeaturedStatus,
  changePackFeaturedForOnboardingStatus,
  changePackMarketplaceApprovalStatus,
  updateRecentHabitIcons,
  addRecentHabitIcon,
  sortRecentHabitIcons,
  updateIsFetchingNextRoutineActivity,
  changeIsTextEditorWithinCharacterLimit,
  updateActiveTabIndex,
  updateIsSettingEditingFinished,
  updateAreSettingsValid,
  updateActiveMarketTab,
  updateAreSettingsSaving,
  resetActiveTabIndex,
  setHighlightShutdownTime,
  setHighlightCutoffTime,
  updateLanguage,
  updateIsUnlockHabitPackModalShown,
  openUnlockHabitPackModal,
  unlockHabitPackActivity,
  copyActivity,
  updateShowPrivacyPolicyModal,
  updateAiTone,
  updateShowSkipActivityModal,
  updateRoutinePlayerStep,
  updateIsLeaderBoardFetching,
  updateIsRequestDataConfirmationModalShown,
  updateIsRequestDataProcessing,
  updateIsAccountDeleteConfirmationModalShown,
  updateIsAccountDeleting,
  updateIsAccountDeleted,
  updateIsUserSettingsModified,
  updateThemeMode,
  updateFont,
  updateIsActivityLibraryUpdating,
  updatePreviousActivityLibrary,
  updateFlags,
  resetFlags,
  updateIsPlatformCoursesSyncing,
  updateValidationErrors,
  updateFeedback,
  updateIsUserOnboarding,
  updateCustomRoutines,
  removeCustomRoutine,
  updateCustomRoutine,
  updateDeviceID,
  updateRoutineTimeValidation,
  updateIsSearchingDevice,
  updateDevice,
  resetIsNewHabitAppended,
  updateIsNewCustomRoutineAppended,
  addFlag,
  removeFlag,
  updateStatsPageActiveTabIndex,
  addHabitToRoutine,
  resetActivityFromRoutineSuggestionsVideoUrls,
  restoreActivityFromRoutineSuggestionsVideoUrls,
  updateIsNewUserSigninProcess,
  updateIsFetchingDeviceSettings,
  updateIsUpdatingDeviceSettings,
  updateIsShowPrivacyPolicyModal
} = setting.actions;

export default setting.reducer;
